// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Indicative portfolio-level risk (Fund Details Enrichment Tier 2, OQ-4):
 * an allocation-weighted mean of the component funds' canonical 1-7 ratings.
 * Purely indicative — unrated components are excluded from the mean and flagged
 * as `partial` so the advisor knows the number is based on a subset.
 */

export interface PortfolioRiskInput {
  riskRating: number | null | undefined;
  /** Allocation weight (%). Non-positive / non-finite weights are treated as 0. */
  weight: number;
}

export interface PortfolioRiskResult {
  /** Weighted mean over rated components, or null when none are rated (or all weights are 0). */
  value: number | null;
  /** value rounded to the nearest 1-7 for chip display. */
  rounded: number | null;
  /** True when at least one component has no rating (the mean covers a subset). */
  partial: boolean;
  ratedCount: number;
  total: number;
}

export function computePortfolioRisk(items: PortfolioRiskInput[]): PortfolioRiskResult {
  let weighted = 0;
  let ratedWeight = 0;
  let ratedCount = 0;
  const total = items.length;

  for (const it of items) {
    const w = Number.isFinite(it.weight) && it.weight > 0 ? it.weight : 0;
    if (it.riskRating != null) {
      ratedCount += 1;
      weighted += it.riskRating * w;
      ratedWeight += w;
    }
  }

  const value = ratedWeight > 0 ? weighted / ratedWeight : null;
  return {
    value,
    rounded: value != null ? Math.round(value) : null,
    partial: ratedCount < total,
    ratedCount,
    total,
  };
}
